import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import { FaSearch, FaCheck, FaTimes, FaClock, FaPlus, FaEye, FaChartBar, FaIdCard } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';
import { toast } from 'react-toastify';

const ManageLicenses = () => {
  const { user } = useAuth();
  
  // State management
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('requests');
  
  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Form states
  const [requestedLicenses, setRequestedLicenses] = useState('');
  const [notes, setNotes] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Status configurations
  const STATUS_CONFIG = {
    pending: { variant: 'warning', icon: FaClock, label: 'Pending' },
    approved: { variant: 'success', icon: FaCheck, label: 'Approved' },
    rejected: { variant: 'danger', icon: FaTimes, label: 'Rejected' }
  };

  // Fetch license requests for organization
  const fetchLicenseRequests = async () => {
    if (!user?.organization_id) return;
    
    try {
      setLoading(true);
      const response = await api.licenses.getOrganizationRequests(user.organization_id, filterStatus);
      if (response.data.success) {
        setLicenseRequests(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching license requests:', error);
      toast.error('Failed to load license requests: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch license usage for organization
  const fetchLicenseUsage = async () => {
    if (!user?.organization_id) return;
    
    try {
      setUsageLoading(true);
      const response = await api.licenses.getOrganizationUsage(user.organization_id);
      if (response.data.success) {
        setLicenseUsage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching license usage:', error);
      toast.error('Failed to load license usage: ' + (error.response?.data?.message || error.message));
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenseRequests();
  }, [filterStatus, user?.organization_id]);

  useEffect(() => {
    if (activeTab === 'usage') {
      fetchLicenseUsage();
    }
  }, [activeTab, user?.organization_id]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleShowRequestModal = () => {
    setRequestedLicenses('');
    setNotes('');
    setFormErrors({});
    setShowRequestModal(true);
  };

  const handleShowDetailsModal = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleCloseModals = () => {
    setShowRequestModal(false);
    setShowDetailsModal(false);
    setSelectedRequest(null);
    setRequestedLicenses('');
    setNotes('');
    setFormErrors({});
  };

  const validateRequestForm = () => {
    const errors = {};
    
    if (!requestedLicenses || requestedLicenses <= 0) {
      errors.requestedLicenses = 'Requested licenses must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRequest = async () => {
    if (!validateRequestForm()) return;

    setModalLoading(true);
    try {
      await api.licenses.createRequest(
        user.organization_id,
        parseInt(requestedLicenses),
        notes.trim() || null
      );
      toast.success('License request submitted successfully!');
      handleCloseModals();
      fetchLicenseRequests();
    } catch (error) {
      console.error('Error creating license request:', error);
      toast.error('Failed to create license request: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  // Filter requests based on search term
  const filteredRequests = licenseRequests.filter(request => {
    const matchesSearch = 
      request.id.toString().includes(searchTerm) ||
      request.requested_licenses.toString().includes(searchTerm) ||
      (request.notes && request.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container-fluid p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Col>
            <Row>
              <Col md={12} className='text-start'>
                <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 text-start">Manage Licenses</h4>
              </Col>
            </Row>
          </Col>
        </div>
        <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-6 border-b-4 border-purple-300 rounded-t-2xl overflow-hidden shadow-2xl">
        <Tab eventKey="requests" title={
          <span className="d-flex align-items-center gap-4 text-1xl font-bold text-purple-700 py-2 px-6 bg-white/50 backdrop-blur">
            <FaClock className="me-2 text-yellow-600" />
            License Requests</span>
        }>
                    <div className="pt-6 md:pt-6"></div>

          {/* Search and Filter Controls */}
          <Row className="mb-3">
            <Col md={6} className="mb-2 mb-md-0">
              <div className="flex shadow-2xl rounded-xl overflow-hidden bg-white border-0 border-purple-300">
                <div className="flex items-center px-6 bg-white">
                  <FaSearch className="w-4 h-4 text-purple-600" />
                </div>
                <input
                  type="text"
                  placeholder="Search by request ID, licenses, or notes..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base form-control"
                />
              </div>
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Select 
                value={filterStatus} 
                onChange={handleFilterChange}
                className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Button
                  bsPrefix="custom-hamburger"
                  onClick={handleShowRequestModal}
                  className="w-100 border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                <FaPlus className="w-4 h-4" />
                <span className="whitespace-nowrap">Request Licenses</span>
              </Button>
            </Col>
          </Row>

          {/* License Requests Table */}
          {loading ? (
            <div className="text-center py-32">
              <Spinner animation="border" role="status" className="text-purple-600 w-16 h-16">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-4 text-1xl font-extrabold text-purple-700">Loading Requests...</p>
            </div>
          ) : (
            <div className="table-responsive overflow-x-auto -mx-3 sm:mx-0 rounded-xl shadow-lg bg-white">
              <Table striped bordered hover className="m-0 min-w-max text-xs sm:text-sm">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">Request ID</th>
                    <th className="py-2 px-3">Licenses Requested</th>
                    <th className="py-2 px-3">Request Date</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Granted Licenses</th>
                    <th className="py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-12">
                        <Alert variant="info" className="bg-purple-50 border-4 border-purple-300 shadow-2xl mx-6 md:mx-20 py-8 mb-0">
                          <p className="text-2xl md:text-3xl font-extrabold text-purple-700 mb-2">No license requests found</p>
                          <p className="text-sm md:text-base text-purple-600">{searchTerm && 'Try adjusting your search terms.'}</p>
                        </Alert>
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => {
                      const statusConfig = STATUS_CONFIG[request.status];
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={request.id} className="border-b border-purple-50 hover:bg-purple-50 transition-all duration-300">
                          <td className="py-2 px-3 align-middle">
                            <strong className="text-1xl font-bold text-purple-700">#{request.id}</strong>
                          </td>
                          <td className="py-2 px-3 align-middle">
                            <span className="fw-bold text-1xl text-purple-700">{request.requested_licenses}</span>
                          </td>
                          <td className="py-2 px-3 align-middle text-gray-700">{formatDate(request.createdAt)}</td>
                          <td className="py-2 px-3 align-middle">
                            <Badge bg={statusConfig.variant} className="px-6 py-2 font-bold shadow-lg">
                              {/* <StatusIcon className="me-2" /> */}
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 align-middle">
                            {request.status === 'approved' ? (
                              <span className="fw-bold text-emerald-600">{request.approved_licenses}</span>
                            ) : request.status === 'rejected' ? (
                              <span className="text-muted">-</span>
                            ) : (
                              <span className="text-muted">Pending</span>
                            )}
                          </td>
                          <td className="py-2 px-3 align-middle">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleShowDetailsModal(request)}
                              className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                              title="View Details"
                            >
                              <FaEye className="w-5 h-5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Tab>

        <Tab eventKey="usage" title={
          <span className="d-flex align-items-center gap-4 text-1xl font-bold text-emerald-700 py-2 px-6 backdrop-blur">
            <FaChartBar className="me-2 text-emerald-600 w-5 h-5" />
            License Usage
          </span>
        }>
                              <div className="pt-6 md:pt-6"></div>
          {/* License Usage Overview */}
          {usageLoading ? (
            <div className="text-center py-32">
              <Spinner animation="border" role="status" className="text-purple-600 w-16 h-16">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-4 text-1xl font-extrabold text-purple-700">Loading Usage Data...</p>
            </div>
          ) : licenseUsage ? (
            <Row>
              <Col md={12}>
                <Card className="shadow-2xl border-0 rounded-3xl">
                  <Card.Header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 rounded-t-3xl">
                    <h6 className="mb-0 text-1xl font-bold">License Usage Overview</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-purple-700 font-extrabold mb-1">{licenseUsage.total_licenses}</h4>
                          <p className="text-gray-600 mb-0">Total Licenses</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-red-600 font-extrabold mb-1">{licenseUsage.used_licenses}</h4>
                          <p className="text-gray-600 mb-0">Used Licenses</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-emerald-600 font-extrabold mb-1">{licenseUsage.remaining_licenses}</h4>
                          <p className="text-gray-600 mb-0">Remaining Licenses</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className={`mb-1 ${
                            licenseUsage.total_licenses > 0 
                              ? Math.round((licenseUsage.used_licenses / licenseUsage.total_licenses) * 100) >= 90 
                                ? 'text-danger' 
                                : Math.round((licenseUsage.used_licenses / licenseUsage.total_licenses) * 100) >= 75 
                                  ? 'text-warning' 
                                  : 'text-info'
                              : 'text-muted'
                          }`}>
                            {licenseUsage.total_licenses > 0 
                              ? Math.round((licenseUsage.used_licenses / licenseUsage.total_licenses) * 100)
                              : 0}%
                          </h4>
                          <p className="text-gray-600 mb-0">Usage Percentage</p>
                        </div>
                      </Col>
                    </Row>
                    
                    {licenseUsage.remaining_licenses === 0 && (
                      <Alert variant="danger" className="mt-3 mb-0 rounded-lg text-sm">
                        <FaTimes className="me-2" />
                        All licenses have been used. Request more licenses to add new users.
                      </Alert>
                    )}
                    
                    {licenseUsage.remaining_licenses > 0 && licenseUsage.remaining_licenses <= 5 && (
                      <Alert variant="warning" className="mt-3 mb-0 rounded-lg text-sm">
                        <FaClock className="me-2" />
                        Only {licenseUsage.remaining_licenses} licenses remaining. Consider requesting more licenses.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <Alert variant="info" className="bg-purple-50 border-purple-200 text-gray-700">
              No license usage data available. Request licenses to get started.
            </Alert>
          )}
        </Tab>
      </Tabs>

      {/* Create License Request Modal */}
      <Modal show={showRequestModal} onHide={handleCloseModals} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Request Licenses</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Number of Licenses Requested <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="1"
                value={requestedLicenses}
                onChange={(e) => setRequestedLicenses(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.requestedLicenses ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                placeholder="Enter number of licenses needed"
              />
              {formErrors.requestedLicenses && <p className="text-red-500 text-xs mt-1">{formErrors.requestedLicenses}</p>}
              <p className="text-xs text-gray-500 mt-1">Specify how many user licenses your organization needs</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Request Notes</label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                placeholder="Add any notes about this request (optional)"
              />
              <p className="text-xs text-gray-500 mt-1">Provide additional context for your license request</p>
            </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModals} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={handleCreateRequest}
                    disabled={modalLoading}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {modalLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FaPlus className="w-4 h-4" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Request Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseModals} centered size="lg">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">License Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          {selectedRequest && (
            <Row>
              <Col md={6}>
                <Card className="mb-3 shadow-2xl border-0 rounded-3xl">
                  <Card.Header className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 rounded-t-3xl">
                    <h6 className="mb-0 text-1xl font-bold">Request Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Request ID:</strong> #{selectedRequest.id}</p>
                    <p><strong>Licenses Requested:</strong> {selectedRequest.requested_licenses}</p>
                    <p><strong>Request Date:</strong> {formatDate(selectedRequest.createdAt)}</p>
                    <p><strong>Status:</strong> 
                      <Badge bg={STATUS_CONFIG[selectedRequest.status].variant} className="ms-2 px-6 py-2 font-bold shadow-lg">
                        {STATUS_CONFIG[selectedRequest.status].label}
                      </Badge>
                    </p>
                    {selectedRequest.notes && (
                      <p><strong>Request Notes:</strong> {selectedRequest.notes}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                {selectedRequest.status !== 'pending' && (
                  <Card className="mb-3 shadow-2xl border-0 rounded-3xl">
                    <Card.Header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-2 rounded-t-3xl">
                      <h6 className="mb-0 text-1xl font-bold">Processing Details</h6>
                    </Card.Header>
                    <Card.Body>
                      {selectedRequest.status === 'approved' && (
                        <>
                          <p><strong>Approved Licenses:</strong> {selectedRequest.approved_licenses}</p>
                          <p><strong>Approved Date:</strong> {formatDate(selectedRequest.approved_at)}</p>
                        </>
                      )}
                      {selectedRequest.status === 'rejected' && (
                        <>
                          <p><strong>Rejection Reason:</strong> {selectedRequest.rejection_reason}</p>
                          <p><strong>Rejected Date:</strong> {formatDate(selectedRequest.approved_at)}</p>
                        </>
                      )}
                      {selectedRequest.approver && (
                        <p><strong>Processed By:</strong> {selectedRequest.approver.first_name} {selectedRequest.approver.last_name}</p>
                      )}
                    </Card.Body>
                  </Card>
                )}
                
                {selectedRequest.status === 'pending' && (
                  <Card className="mb-3 shadow-2xl border-0 rounded-3xl">
                    <Card.Header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 rounded-t-3xl">
                      <h6 className="mb-0 text-1xl font-bold">Status</h6>
                    </Card.Header>
                    <Card.Body>
                      <Alert variant="info" className="mb-0 bg-purple-50 border-purple-200 text-gray-700">
                        <FaClock className="me-2" />
                        Your request is pending review by the system administrator. You will be notified once it has been processed.
                      </Alert>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Button bsPrefix="custom-hamburger" onClick={handleCloseModals} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageLicenses;
